import * as React from 'react'

import { clsx } from 'clsx'

import { ExternalLinkIcon, IconButton, Text } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'

import { ExpandableEngagementText } from '../../components/ExpandableEngagementText'
import { useMentionData } from '../context/MentionContext'
import { MentionedChannelText } from '../../components/MentionedChannelText'
import type { MentionedChannel } from '../../util/getMentionedChannelTextParts'

import carouselStyles from '../../shared/CarouselBadge.module.css'
import styles from './MentionPostMiniature.module.css'

import type { JSX } from 'react'

/**
 * What this miniature reads from a `mentionType: post` mention — the post
 * snapshot lives on the mention's own fields. Spread by every query whose
 * mentions can render through this component.
 */
export const MentionPostMiniature_Mention = graphql(/* GraphQL */ `
  fragment MentionPostMiniature_Mention on Mention @_unmask {
    text
    publicUrl
    contentType
    author {
      name
      username
    }
    assets {
      __typename
      thumbnail
      ... on ImageAsset {
        image {
          altText
        }
      }
    }
  }
`)

type MiniatureImageAsset = Extract<
  FragmentOf<typeof MentionPostMiniature_Mention>['assets'][number],
  { __typename: 'ImageAsset' }
>

/**
 * Narrows an asset to the `ImageAsset` branch. The mention still arrives
 * through context typed as the broad entity, whose `assets` interface
 * carries no `__typename` discriminant — once the context goes
 * fragment-first this becomes plain discriminated-union narrowing.
 */
const isImageAsset = (asset: {
  __typename?: string
  thumbnail: string
}): asset is MiniatureImageAsset => asset.__typename === 'ImageAsset'

/**
 * Inner post-miniature card: the post's caption and media, plus a
 * hover-revealed link to the post on the network. It consumes a
 * `Mention`-shaped snapshot (third-party source post) rather than a Buffer
 * post fragment. The post IS a `mentionType: post` mention, so the snapshot
 * always comes from the mention's own fields. This serves both call sites:
 *  - a post-type mention body (the card's own mention), and
 *  - the comment-type parent-post miniature, where the conversation `post`
 *    (itself a `mentionType: post` mention) is provided via its own
 *    `MentionContextProvider` — see `MentionCard`.
 *
 * TODO(mentions-BE-gap): reel/story badge not rendered. Waiting on BE
 * to add `story` (in flight) and possibly `reel` to
 * `EngagementContentType`, then render via `PostTypeBadge`.
 */
type MentionPostMiniatureProps = {
  /** The mentioned channel; its name is highlighted in the caption. */
  channel: MentionedChannel
}

export const MentionPostMiniature = ({
  channel,
}: MentionPostMiniatureProps): JSX.Element => {
  const post = useMentionData()

  // The author can be null — BE returns null for locked/deleted accounts.
  // Nothing names them visually any more (the card header above does), so
  // this only feeds the external link's accessible name.
  const author = post.author
  const authorLabel = author?.name ?? author?.username ?? 'Unknown author'
  const hasText = Boolean(post.text && post.text.trim().length > 0)
  const firstAsset = post.assets[0] ?? null
  const extraAssetCount = Math.max(0, post.assets.length - 1)
  // Network media is served from short-lived urls, so the thumbnail can be
  // gone by the time the miniature renders. Drop the whole media slot then —
  // a lone "+2" counts media nobody can see. Tracked by url so a re-hosted
  // asset gets a fresh attempt.
  const [unreachableThumbnail, setUnreachableThumbnail] = React.useState<
    string | null
  >(null)
  const hasMedia =
    firstAsset !== null && firstAsset.thumbnail !== unreachableThumbnail
  // `altText` only lives on `ImageAsset.image`. For `VideoAsset` and
  // `DocumentAsset` (and any image without provided alt text), the
  // thumbnail stays decorative — the caption and the card header above
  // already give the screen reader enough context.
  const thumbnailAlt =
    firstAsset && isImageAsset(firstAsset)
      ? (firstAsset.image.altText ?? '')
      : ''

  return (
    <article
      className={styles.card}
      data-testid="mention-post-miniature"
      data-content-type={post.contentType ?? 'unknown'}
      data-media-only={hasMedia && !hasText ? 'true' : 'false'}
    >
      {post.publicUrl && (
        <IconButton
          as="a"
          href={post.publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          label={`Open ${authorLabel}'s post in a new tab`}
          variant="secondary"
          size="small"
          className={styles.externalLink}
          data-testid="mention-post-miniature-link"
          // The miniature sits inside `MentionWrapper` (role=button),
          // so a click here would otherwise bubble up and toggle the
          // card. Localize the click to the link itself.
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLinkIcon />
        </IconButton>
      )}
      <div className={styles.body}>
        {hasText ? (
          <ExpandableEngagementText>
            <Text as="p" size="sm" className={styles.caption} translate="no">
              <MentionedChannelText text={post.text ?? ''} channel={channel} />
            </Text>
          </ExpandableEngagementText>
        ) : (
          <Text size="sm" color="subtle" className={styles.captionPlaceholder}>
            This post has no caption
          </Text>
        )}
      </div>
      {hasMedia && (
        <div className={styles.assetsContainer}>
          <img
            className={styles.assets}
            src={getProxiedThumbnailUrl(firstAsset.thumbnail)}
            alt={thumbnailAlt}
            aria-hidden={thumbnailAlt === '' ? true : undefined}
            data-testid="mention-post-miniature-thumbnail"
            onError={(): void => setUnreachableThumbnail(firstAsset.thumbnail)}
          />
          {extraAssetCount > 0 && (
            <span
              className={clsx(
                carouselStyles.carouselBadge,
                carouselStyles.small,
              )}
              data-testid="mention-post-miniature-carousel-badge"
            >
              +{extraAssetCount}
            </span>
          )}
        </div>
      )}
    </article>
  )
}

MentionPostMiniature.displayName = 'MentionPostMiniature'
